/**
 * useTestDefinitions.ts
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md) — data hook for the
 * rebuilt Test Catalogue page. Loads everything the master-detail view needs:
 *
 *   - the unified test-definitions payload (items + operational prep steps)
 *   - active machines + the capable-machine map (Default Machine picker)
 *   - active skills (Required Skill autocomplete)
 *   - active task types (Advanced recipe "add step" autocomplete)
 *
 * The definitions payload is the source of truth after every mutation —
 * mutations live in the page (services layer) and call refresh().
 */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchTestDefinitions,
  type TestDefinitionItem,
  type TestDefinitionTaskType,
} from '../../../services/testDefinitionService';
import { fetchCapableMachines } from '../../../services/testCatalogService';
import { fetchMachines } from '../../../services/scheduleMachines';
import { fetchSkills, fetchTaskTypes } from '../../../services/scheduleTaxonomy';
import type { Skill, TaskType } from '../../../types/schedule';
import type { MachineOption } from './testCatalogHelpers';

export interface TestDefinitionsData {
  items: TestDefinitionItem[];
  operationalTaskTypes: TestDefinitionTaskType[];
  machineOptions: MachineOption[];
  /** Capable-machine map: lower-cased test key → lower-cased machine keys. */
  capableMap: Record<string, string[]>;
  skills: Skill[];
  allTaskTypes: TaskType[];
  loading: boolean;
  error: string | null;
  /** Re-fetch the definitions payload (after any mutation). */
  refresh: () => Promise<void>;
  /** Re-fetch the side lists too (after Machines & Time / Skills edits). */
  refreshLookups: () => Promise<void>;
}

export function useTestDefinitions(): TestDefinitionsData {
  const [items, setItems] = useState<TestDefinitionItem[]>([]);
  const [operationalTaskTypes, setOperationalTaskTypes] = useState<TestDefinitionTaskType[]>([]);
  const [machineOptions, setMachineOptions] = useState<MachineOption[]>([]);
  const [capableMap, setCapableMap] = useState<Record<string, string[]>>({});
  const [skills, setSkills] = useState<Skill[]>([]);
  const [allTaskTypes, setAllTaskTypes] = useState<TaskType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const payload = await fetchTestDefinitions();
      setItems(payload.items);
      setOperationalTaskTypes(payload.operationalTaskTypes);
      setError(null);
    } catch {
      setError('Failed to load the test catalogue — reload and retry.');
    }
  }, []);

  const refreshLookups = useCallback(async () => {
    // Non-fatal: pickers just show fewer options if a lookup fails.
    const [machines, capable, skillList, taskTypeList] = await Promise.allSettled([
      fetchMachines(),
      fetchCapableMachines(),
      fetchSkills(),
      fetchTaskTypes(),
    ]);
    if (machines.status === 'fulfilled') {
      setMachineOptions(
        machines.value
          .filter((m) => m.IsActive)
          .map((m) => ({ value: m.MachineKey, label: `${m.Code} — ${m.Name}` })),
      );
    }
    if (capable.status === 'fulfilled') setCapableMap(capable.value);
    if (skillList.status === 'fulfilled') setSkills(skillList.value);
    if (taskTypeList.status === 'fulfilled') setAllTaskTypes(taskTypeList.value);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([refresh(), refreshLookups()]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refresh, refreshLookups]);

  return {
    items,
    operationalTaskTypes,
    machineOptions,
    capableMap,
    skills,
    allTaskTypes,
    loading,
    error,
    refresh,
    refreshLookups,
  };
}
